import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { loadStripe } from '@stripe/stripe-js';
import { createApiUrl } from '../utils/apiConfig';
import { useAuth } from '../context/AuthContext';
import './Payment.css';

// Get the Stripe key from the server
const getStripeKey = async () => {
  try {
    const response = await axios.get(createApiUrl('/api/stripe-key'));
    return response.data.publishableKey;
  } catch (error) {
    console.error('Error fetching Stripe key:', error);
    return null;
  }
};

const Payment = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [basketItems, setBasketItems] = useState([]);
  const [connectionStatus, setConnectionStatus] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('invoice');
  const [stripePromise, setStripePromise] = useState(null);
  const [showStripeLimitModal, setShowStripeLimitModal] = useState(false);

  useEffect(() => {
    const fetchBasketItems = async () => {
      try {
        const response = await axios.get(createApiUrl('/api/user-and-basket'), {
          withCredentials: true
        });
        setBasketItems(response.data.basket || []);
      } catch (error) {
        console.error('Error fetching basket items:', error);
        setConnectionStatus('Error connecting to server');
      }
    };

    const initializeStripe = async () => {
      try {
        const publishableKey = await getStripeKey();
        if (publishableKey) {
          setStripePromise(loadStripe(publishableKey));
        }
      } catch (error) {
        console.error('Error initializing Stripe:', error);
      }
    };

    fetchBasketItems();
    initializeStripe();
    checkConnection();
  }, []);

  const checkConnection = () => {
    if (!navigator.onLine) {
      setConnectionStatus('You are offline');
    } else {
      setConnectionStatus('');
    }
  };

  const formatDuration = (duration) => {
    if (duration < 30) {
      return `${duration} days`;
    } else if (duration % 365 === 0) {
      const years = duration / 365;
      return `${years} year${years > 1 ? 's' : ''}`;
    } else {
      return `${Math.ceil(duration / 30)} months`;
    }
  };

  const handlePayment = async (item) => {
    if (
      paymentMethod === 'card' &&
      item.type === 'subscription' &&
      item.duration === 1825
    ) {
      setShowStripeLimitModal(true);
      return;
    }
    try {
      const requestBody = {
        planId: item.planId,
        price: item.price,
        totalPrice: item.price * item.quantity * (1 - (item.discount || 0)),
        paymentMethod: paymentMethod,
        workshopDate: item.workshopDate,
        discountCode: item.discountCode,
        quantity: item.quantity
      };

      const response = await axios.post(createApiUrl('/api/create-checkout-session'), requestBody, {
        withCredentials: true
      });

      if (paymentMethod === 'invoice') {

        
        if (response.data.message) {
          if (user?.isOrgAdmin || user?.userType === 'organization') {

            navigate(`/org-admin-dashboard?message=${encodeURIComponent(response.data.message)}`);
          } else if (user?.isSchAdmin) {

            navigate(`/school-admin-dashboard?message=${encodeURIComponent(response.data.message)}`);
          } else {

            navigate(`/teacher-dashboard?message=${encodeURIComponent(response.data.message)}`);
          }
        } else {
          if (user?.isOrgAdmin || user?.userType === 'organization') {

            navigate('/org-admin-dashboard');
          } else if (user?.isSchAdmin) {

            navigate('/school-admin-dashboard');
          } else {

            navigate('/teacher-dashboard');
          }
        }
      } else {
        if (!stripePromise) {
          alert('Payment system is not ready. Please try again in a moment.');
          return;
        }
        const stripe = await stripePromise;
        if (item.price === 0) {
          alert('30-Day Free Trial activated successfully');
          if (user?.isOrgAdmin) {
            navigate('/org-admin-dashboard');
          } else if (user?.isSchAdmin) {
            navigate('/school-admin-dashboard');
          } else {
            navigate('/teacher-dashboard');
          }
        } else {
          await stripe.redirectToCheckout({ sessionId: response.data.id });
        }
      }
    } catch (error) {
      console.error('Error:', error);
      alert('Failed to create checkout session');
    }
  };

  const handleRemovePlan = async (planId) => {
    try {

      
      const response = await axios.post(createApiUrl('/api/remove-asset-from-basket'), { 
        planId
      }, {
        withCredentials: true
      });
      
      
      // Use the redirect URL from the backend response
      if (response.data.success && response.data.redirectUrl) {
        navigate(response.data.redirectUrl);
      } else {
        // Fallback to appropriate dashboard based on user type
        const isOrgAdmin = user?.isOrgAdmin || user?.userType === 'organization';
        if (isOrgAdmin) {
          navigate('/org-admin-dashboard');
        } else if (user?.isSchAdmin) {
          navigate('/school-admin-dashboard');
        } else {
          navigate('/teacher-dashboard');
        }
      }
    } catch (error) {
      console.error('Error:', error);
      alert('Failed to remove plan');
      // Fallback redirect on error based on user type
      const isOrgAdmin = user?.isOrgAdmin || user?.userType === 'organization';
      if (isOrgAdmin) {
        navigate('/org-admin-dashboard');
      } else if (user?.isSchAdmin) {
        navigate('/school-admin-dashboard');
      } else {
        navigate('/teacher-dashboard');
      }
    }
  };

  return (
    <div className="payment-container payment-page">
      <h1 className="payment-title">Payment</h1>
      {connectionStatus && (
        <div className="connection-status">{connectionStatus}</div>
      )}

      {/* Stripe 5-year subscription modal */}
      {showStripeLimitModal && (
        <div className="stripe-limit-modal-overlay">
          <div className="stripe-limit-modal">
            <h2>Stripe Limitation</h2>
            <p>Stripe only supports subscriptions up to 3 years.<br/>Please choose <b>Invoice</b> payment for 5-year subscriptions.</p>
            <button className="btn payment-btn payment-btn-primary" onClick={() => setShowStripeLimitModal(false)}>OK</button>
          </div>
        </div>
      )}

      <section>
        <div className="text-center my-4">
          <img 
            src="/img/kbhClassroomLogo.png" 
            alt="KBH Classroom logo" 
            className="payment-img-fluid" 
            width="250" 
            height="150"
          />
        </div>
        <div><p></p></div>
        <br />

        {basketItems && basketItems.length > 0 ? (
          basketItems.map((item, index) => (
            <div key={index} className="payment-product">
              <h4>{item.name}</h4>
              <p>Duration: {formatDuration(item.duration)}</p>
              <p>Price: {item.price.toFixed(2)}</p>
              {item.quantity && <p>Quantity: {item.quantity}</p>}
              {item.discount > 0 && (
                <p>Discount: {(item.discount * 100).toFixed(0)}%</p>
              )}
              <p>Total Price: {((item.price || 0) * (item.quantity || 1) * (1 - (item.discount || 0))).toFixed(2)}</p>
              
              {item.type === 'workshop' && (
                <p>
                  Workshop Date:{' '}
                  {new Date(item.workshopDate).toLocaleDateString('en-GB', {
                    weekday: 'short',
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric'
                  })}
                </p>
              )}

              <div className="payment-row">
                {(!item.assets || item.assets.length === 0) && item.type !== 'workshop' && (
                  <p>No assets available.</p>
                )}
              </div>
              <div><p></p></div>

              <div className="payment-controls">
                <div className="payment-method">
                  <label htmlFor="paymentMethod" className="payment-label">
                    Choose a payment method:
                  </label>
                  <select
                    id="paymentMethod"
                    value={paymentMethod}
                    onChange={(e) => setPaymentMethod(e.target.value)}
                  >
                    <option value="invoice">Invoice</option>
                    <option value="card">Card</option>
                  </select>
                </div>

                <div className="button-container">
                  <button
                    type="button"
                    className="btn payment-btn payment-btn-primary"
                    onClick={() => {
                      if (
                        paymentMethod === 'card' &&
                        item.type === 'subscription' &&
                        item.duration === 1825
                      ) {
                        setShowStripeLimitModal(true);
                        return;
                      }
                      handlePayment(item);
                    }}
                  >
                    Proceed to Stripe Payment
                  </button>
                  <button
                    type="button"
                    className="btn payment-btn payment-btn-danger"
                    onClick={() => handleRemovePlan(item.planId)}
                  >
                    Remove Product
                  </button>
                </div>
              </div>
            </div>
          ))
        ) : (
          <p>Your basket is empty.</p>
        )}
      </section>
    </div>
  );
};

export default Payment; 